<template>
  <table class="table">
    <div class="thead">
      <div class="tr">
        <div class="th empty"></div>
        <div
          class="th"
          v-for="(date, index) in weekDates"
          :class="isItToday(date.dateTimeMS)"
          :key="index"
          :data-index="index + 1"
          :data-date="date.dateNum"
          :data-year="date.yearNum"
          :data-month="date.monthNum"
        >
          <span class="day"> {{ date.dayNameShort }}</span>
          <span class="date">{{ date.dateNum }}</span>
        </div>
      </div>
    </div>
    <tbody v-if="calendarData" class="table-rows" :style="$windowWidth <= 1280 && selectedDateTimes.length ? 'padding-bottom: 205px;' : ''">
      <tr v-for="(rowArray, rowIndex) in calendarDateTimes" :key="rowIndex" :data-rowhour="getDataRowHour(rowIndex)" :style="getSelectedHeights">
        <td :class="$isMobile ? 'td--first--mobile' : ''">
          <div v-if="isFullHour(rowIndex)" class="hour">
            {{ getRowStartTime(rowIndex) }}
          </div>
        </td>
        <td
          v-for="(dateTimeMS, columnIndex) in rowArray"
          :key="columnIndex"
          class="each-day"
          :class="isPastHour(dateTimeMS) ? 'past-day' : ''"
          :data-daynum="columnIndex"
        >
          <CurrentHourLine v-if="isCurrentHourSlot(dateTimeMS)" :slotLinePercentage="getLinePercentage(dateTimeMS)" />
          <!-- TODO: Set attendedSuccessful/not in here too, to display 'MISSED' if necessary -->
          <PastSession
            v-if="isPastHour(dateTimeMS) && userHadMatchedSessionForSlot(dateTimeMS)"
            :boxHeight="getBoxHeights"
            :slotTimeString="getSlotTimes(dateTimeMS)"
            :partnerData="getPastPartnerData(dateTimeMS)"
          />
          <PastSlot v-if="isPastHour(dateTimeMS)" :slotLinePercentage="getLinePercentage(dateTimeMS)" />
          <div class="general-container" v-else-if="!isPastHour(dateTimeMS) && calendarData[dateTimeMS]">
            <SlotHoverEmpty
              v-if="!calendarData[dateTimeMS].isSelected && calendarData[dateTimeMS].isAvailableForBooking"
              :boxHeight="getBoxHeights"
              :heightNum="height"
              :slotDateTimeMS="dateTimeMS"
              :hasSomeone="hasSomeOne(dateTimeMS)"
              @handleSlotSelection="handleSlotSelection"
            />
            <!-- TODO rename -->
            <UserSession
              v-if="userHasSessionForSlot(dateTimeMS) && !calendarData[dateTimeMS].isCanceling"
              :userIsMatchedForSlot="userIsMatchedForSlot(dateTimeMS)"
              :sessionLink="joinSessionLink(dateTimeMS)"
              :userID="user._id"
              :boxHeight="getBoxHeights"
              :minutesToNextSession="minutesToNextSession"
              :nextMatchDateTimeMS="nextMatchDateTimeMS"
              :slotTimeString="getSlotTimes(dateTimeMS)"
              :partnerData="getMatchedPartnerData(dateTimeMS)"
              :slotDateTimeMS="dateTimeMS"
              :currentUserData="currentUserData"
              @cancelSession="cancelSession"
              @setIsCanceling="setIsCanceling"
              @exitIsCanceling="exitIsCanceling"
            />
            <UnmatchedPerson
              v-else-if="hasSomeOne(dateTimeMS) && !slotBeforeIsSelected(dateTimeMS) && calendarData[dateTimeMS].isAvailableForBooking"
              :bestMatchInfo="getBestMatchInfo(dateTimeMS)"
              :slotDateTimeMS="dateTimeMS"
              :week="week"
              @handleSlotSelection="handleSlotSelection"
            />
            <BookSession
              v-if="calendarData[dateTimeMS].isSelected"
              :boxHeight="getBoxHeights"
              :slotTimeString="getSlotTimes(dateTimeMS)"
              :bestMatchData="getBestMatchData(dateTimeMS)"
              :slotDateTimeMS="dateTimeMS"
              @bookSession="bookSession"
              @cancelSlot="cancelSlot"
            />
            <CancelSession
              v-if="calendarData[dateTimeMS].isCanceling"
              :userID="user._id"
              :boxHeight="getBoxHeights"
              :slotDateTimeMS="dateTimeMS"
              :quickCancel="false"
              :slotTimeString="getSlotTimes(dateTimeMS)"
              :currentUserData="currentUserData"
              @cancelSession="cancelSession"
              @exitIsCanceling="exitIsCanceling"
            />
          </div>
        </td>
      </tr>
      <LastHourRows :boxHeight="getSelectedHeights" :week="week" :viewStartMS="viewStartMS" :lastMinMS="lastMinMS" />
    </tbody>
    <div class="last-line"></div>
    <div class="table-bottom" :class="$isMobile ? 'table-bottom--mobile' : ''"></div>
    <div class="table--shadow"></div>
  </table>
</template>

<script>
import { mapState } from "vuex";
import { format } from "date-fns";

import BookSession from "./TableComponents/BookSession";
import CancelSession from "./TableComponents/CancelSession";
import CurrentHourLine from "./TableComponents/CurrentHourLine";
import LastHourRows from "./TableComponents/LastHourRows";
import PastSession from "./TableComponents/PastSession";
import PastSlot from "./TableComponents/PastSlot";
import SlotHoverEmpty from "./TableComponents/SlotHoverEmpty";
import UnmatchedPerson from "./TableComponents/UnmatchedPerson";
import UserSession from "./TableComponents/UserSession";

import { SLOT_INTERVAL_MS, SLOT_INTERVAL_MIN, SESSION_LENGTH_MS } from "@/constants_calendar";
import { hr_minutes, dateNum, dayNameShort, monthNum, yearNum, formatSlotDate } from "@/utilities/dateFormatter";

import { StoreMatchController } from "@/store/calendar";
import { CalendarStore } from "../../../ELYX/ELYXStore";
import { SharedSessionUtilities } from "@shared";
import CalendarUtilities from "../../../ELYX/Calendar/CalendarUtilities";

const FIFTEEN_MINUTES = 900000;
const ONE_DAY = 86400000;

export default {
  name: "Table",
  data() {
    return {
      interval: 50,
      // height: 105 / 4,
      // selectedHeight: 105,
      // boxHeight: 105,

      height: 115 / 4,
      selectedHeight: 115,
      boxHeight: 115,
    };
  },
  components: {
    BookSession,
    CancelSession,
    UnmatchedPerson,
    SlotHoverEmpty,
    UserSession,
    PastSlot,
    PastSession,
    LastHourRows,
    CurrentHourLine,
  },
  props: [
    "viewStartMS",
    "viewEndMS",
    "selectedDateTimes",
    "currentlyBooking",

    "minutesToNextSession",
    "nextMatchDateTimeMS",
    "week",
    "lastMinMS",
    "prefer24HourFormat",

    "currentUserData",
    "calendarData",
    "pastMatches",
    "upcomingMatches",
    "matchPool",
    "calendarUsers",
  ],
  computed: {
    bookingData: () => CalendarStore.state,
    bookingLimit: () => CalendarStore.state.bookingLimit,

    ...mapState({
      user: (state) => state.auth.user,
    }),

    weekDates() {
      const dates = [];
      for (let date = this.viewStartMS; date < this.viewEndMS; date += ONE_DAY) {
        dates.push({
          dateTimeMS: date,
          dateNum: dateNum(date),
          monthNum: monthNum(date),
          yearNum: yearNum(date),
          dayNameShort: dayNameShort(date),
        });
      }
      return dates;
    },
    getHeights() {
      let height = this.height - 20;
      let heights = `line-height:${this.height}px; height:${height}px;`;
      return heights;
    },
    getSelectedHeights() {
      let height = this.height;
      height = `height:${height}px;`;
      return height;
    },
    getBoxHeights() {
      let height = this.boxHeight;
      height = `height:${height}px;`;
      return height;
    },
    /*
      this contains all the slot start times in ms as precalculated values,
      the calendar is rendered by iterating over this array
    */
    calendarDateTimes() {
      let calendarArray = [];
      const nrColumns = this.week ? 7 : 1;
      const nrRows = (24 * 60) / SLOT_INTERVAL_MIN;
      const startDateMS = this.viewStartMS;
      for (let i = 0; i < nrRows; i++) {
        let tempArray = [];
        for (let j = 0; j < nrColumns; j++) {
          tempArray.push(startDateMS + j * ONE_DAY + i * SLOT_INTERVAL_MS);
        }
        calendarArray.push(tempArray);
      }
      // console.table(calendarArray);
      return calendarArray;
    },
  },
  methods: {
    /* ================ DISPLAY ================ */

    isItToday(date) {
      return this.lastMinMS >= date && this.lastMinMS < date + ONE_DAY && this.week ? "todayHead" : "";
    },
    getDataRowHour(rowIndex) {
      return format(this.viewStartMS + rowIndex * SLOT_INTERVAL_MS, "HH:mm");
    },
    getRowStartTime(rowIndex) {
      return hr_minutes(this.viewStartMS + rowIndex * SLOT_INTERVAL_MS, this.prefer24HourFormat);
    },
    isPastHour(dateTimeMS) {
      if (this.lastMinMS < dateTimeMS || (this.nextMatchDateTimeMS <= dateTimeMS && dateTimeMS <= this.nextMatchDateTimeMS + SESSION_LENGTH_MS)) {
        return false;
      }
      return true;
    },
    isFullHour(rowIndex) {
      return rowIndex % (60 / SLOT_INTERVAL_MIN) === 0;
    },
    isCurrentHourSlot(dateTimeMS) {
      return dateTimeMS < this.lastMinMS && this.lastMinMS < dateTimeMS + SLOT_INTERVAL_MS;
    },
    getLinePercentage(dateTimeMS) {
      return ((this.lastMinMS - dateTimeMS) * 100) / SLOT_INTERVAL_MS;
    },
    getSlotTimes(dateTimeMS) {
      const slotStartTime = formatSlotDate(dateTimeMS, this.prefer24HourFormat);
      const slotEndTime = formatSlotDate(dateTimeMS + SESSION_LENGTH_MS, this.prefer24HourFormat);
      return slotStartTime + "-" + slotEndTime;
    },

    /* ================ PAST MATCHES ================ */

    userHadMatchedSessionForSlot(dateTimeMS) {
      const pastMatch = this.pastMatches[dateTimeMS];
      return !!pastMatch && pastMatch.partnerID;
    },
    getPastPartnerData(dateTimeMS) {
      const partnerID = this.pastMatches[dateTimeMS].partnerID;
      if (this.calendarUsers[partnerID] && this.calendarUsers[partnerID].metadata) {
        return {
          partnerID,
          partnerName: this.calendarUsers[partnerID].metadata.displayName,
        };
      }
      return null;
    },

    /* ================ UPCOMING MATCHES ================ */

    userHasSessionForSlot(dateTimeMS) {
      return !!this.upcomingMatches[dateTimeMS];
    },
    getMatchedPartnerData(dateTimeMS) {
      const partnerID = this.upcomingMatches[dateTimeMS].partnerID;
      // console.log("@getMatchedPartnerData ID: ", partnerID);
      // console.log("@getMatchedPartnerData partner data: ", this.calendarUsers[partnerID]);
      if (this.calendarUsers[partnerID] && this.calendarUsers[partnerID].metadata) {
        return {
          partnerID,
          partnerName: this.calendarUsers[partnerID].metadata.displayName,
          partnerProfileImageUrl: this.calendarUsers[partnerID].metadata.profileImageUrl,
        };
      }
      return null;
    },
    userIsMatchedForSlot(dateTimeMS) {
      return this.upcomingMatches[dateTimeMS] && this.upcomingMatches[dateTimeMS].partnerID;
    },
    joinSessionLink(dateTimeMS) {
      const sessionID = this.upcomingMatches[dateTimeMS] ? this.upcomingMatches[dateTimeMS].SID : null;
      let sessionLink = "/session/" + sessionID;
      return sessionLink;
    },

    /* ================ MATCH POOL ================ */

    hasSomeOne(dateTimeMS) {
      const matchPoolArray = this.matchPool[dateTimeMS];
      if (matchPoolArray && matchPoolArray.length) return true;
      return null;
    },
    getBestMatchData(dateTimeMS) {
      const matchPoolArray = this.matchPool[dateTimeMS];
      if (matchPoolArray && matchPoolArray.length) {
        console.log("Matchpool rerendered: ", matchPoolArray.length);
        const partnerID = matchPoolArray[0].matchableID;
        if (this.calendarUsers[partnerID]) {
          const matchedUserData = {
            metadata: this.calendarUsers[partnerID].metadata,
            preferences: this.calendarUsers[partnerID].preferences,
            session: matchPoolArray[0].session,
          };
          return matchedUserData;
        }
      }
      return null;
    },
    getBestMatchInfo(dateTimeMS) {
      const matchPoolArray = this.matchPool[dateTimeMS];
      if (matchPoolArray && matchPoolArray.length) {
        const partnerID = matchPoolArray[0].matchableID;
        let partnerData = {};
        const partner = this.calendarUsers[partnerID];
        if (partner) {
          partnerData.metadata = partner.metadata;
          partnerData.preferences = partner.preferences;
          return partnerData;
        } else {
          return null;
        }
      }
      return null;
    },

    /* ================ SELECTABILITY ================ */

    /**
     * returns true, if a selected slot is less than 45 min before dateTimeMS provided in argument
     */
    slotBeforeIsSelected(dateTimeMS) {
      for (let i = 0; i < this.selectedDateTimes.length; i++) {
        if (this.selectedDateTimes[i] >= dateTimeMS) {
          // sorted!
          return false;
        }
        if (this.selectedDateTimes[i] >= dateTimeMS - 3 * FIFTEEN_MINUTES) {
          return true;
        }
      }
    },

    /* ================ EVENTS ================ */
    handleSlotSelection(slotDateTimeMS) {
      if (!slotDateTimeMS) {
        return;
      }

      const attemptingToBookCount = this.selectedDateTimes.length + 1; // already selected + new selection
      const bookingData = this.bookingData;
      bookingData.isUserPremium = this.user.isUserPremium;
      const bookingStatusCode = SharedSessionUtilities.getBookingStatusCode(bookingData, attemptingToBookCount);
      if (SharedSessionUtilities.isUserAllowedToBook(bookingStatusCode)) {
        this.$store.dispatch("selections/selectSlot", slotDateTimeMS);
      } else {
        CalendarUtilities.displayBookingRestrictionModal(bookingStatusCode);
      }
    },
    setIsCanceling(obj) {
      this.$store.dispatch("calendar/setOneSlotFieldStatus", {
        dateTimeMS: obj.dateTimeMS,
        field: "isCanceling",
        value: obj.value,
      });
    },
    async bookSession(dateTimeMS) {
      await StoreMatchController.bookSession(dateTimeMS);
    },
    cancelSlot(dateTimeMS) {
      this.$store.dispatch("selections/cancelSelection", dateTimeMS);
    },
    handleSuccessfulBooking(data) {
      StoreMatchController.handleSuccessfulBooking(data.dateTimeMS, data.SID, null, data.matchedUserData);
    },
    cancelSession(dateTimeMS, userID) {
      StoreMatchController.cancelSession(dateTimeMS, userID);
    },
    exitIsCanceling(dateTimeMS) {
      console.log("@exitIsCanceling dtms: ", dateTimeMS);
      this.$store.dispatch("calendar/setOneSlotFieldStatus", {
        dateTimeMS,
        field: "isCanceling",
        value: false,
      });
    },
  },
};
</script>

<style scoped>
.table-bottom--mobile {
  border-bottom-left-radius: 0px;
  border-bottom-right-radius: 0px;
}
</style>
